import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MatDialog } from '@angular/material/dialog';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ConfirmAcaoRecorrenteModalComponent } from '../../../shared/components/confirm-acao-recorrente-modal/confirm-acao-recorrente-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ModalRenegociacaoParcelasComponent } from './components/modal-renegociacao-parcelas/modal-renegociacao-parcelas.component';
import { ContasReceberDadosGeraisComponent } from './components/contas-receber-dados-gerais/contas-receber-dados-gerais.component';
import { ContasReceberCentroCustoComponent } from './components/contas-receber-centro-custo/contas-receber-centro-custo.component';
import { ContasReceberParcelamentoComponent } from './components/contas-receber-parcelamento/contas-receber-parcelamento.component';
import { ContasReceberPrevisaoComponent } from './components/contas-receber-previsao/contas-receber-previsao.component';
import { ContasReceberComplementaresComponent } from './components/contas-receber-complementares/contas-receber-complementares.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { TipoProcessamentoRecorrenciaEnum } from '../../../shared/enums/financas/tipo-processamento-recorrencia.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ContasReceberService } from '../../../shared/services/apis/financas/contas-receber.service';
import { ConfiguracaoCampoObrigatorioService } from '../../../shared/services/apis/financas/configuracao-campo-obrigatorio.service';
import { PessoaService } from '../../../shared/services/apis/pessoa/pessoa.service';
import { MunicipioService } from '../../../shared/services/apis/usuario/municipio.service';
import { GetLookupsContasReceberResponse } from '../../../shared/services/responses/financas/get-lookups-contas-receber.response';
import { SacadoBoletoStruct } from '../../../shared/services/structs/financas/sacado-boleto.struct';
import { CodigoSefazEnum } from '../../../shared/enums/financas/codigo-sefaz.enum';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MaskService } from '../../../shared/services/mask.service';
import { TelaFinancasEnum } from '../../../shared/enums/financas/tela-financas.enum';
import { CampoObrigatorioStruct } from '../../../shared/services/structs/financas/campo-obrigatorio.struct';

import { ViewChild } from '@angular/core';
import { UtilService } from '../../../shared/services/util.service';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
@Component({
  selector: 'app-contas-receber-cadastro',
  standalone: true,
  imports: [
    AlertCardComponent,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    HeaderComponent,
    FooterActionComponent,
    PageScrollSpyComponent,
    LoadingBoxComponent,
    ContasReceberDadosGeraisComponent,
    ContasReceberCentroCustoComponent,
    ContasReceberParcelamentoComponent,
    ContasReceberPrevisaoComponent,
    ContasReceberComplementaresComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './contas-receber-cadastro.component.html',
  styleUrl: './contas-receber-cadastro.component.css',
})
export class ContasReceberCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Contas_Receber;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public isLoadingLookups: boolean = false;
  public isSaving: boolean = false;
  public idContasReceber: number | null = null;
  public idContasReceberParcela: number | null = null;
  public qtdParcelas: number = 0;
  public isAgrupado: boolean = false;
  /** Pedido de venda que gerou o documento: a tela fica em leitura (baixa, cobrança, tags e categoria seguem pela lista). */
  public idVenda: number | null = null;
  /** NF-e de saída que gerou o documento: a mesma leitura do pedido de venda; alterar ou excluir só pela nota. */
  public idNFeSaida: number | null = null;
  public numeroDocumentoVenda: string | null = null;

  /** Gerado por um documento (pedido de venda ou NF-e de saída): a tela abre em leitura e o Salvar fica desabilitado. */
  public get isOrigemDocumento(): boolean {
    return !!this.idVenda || !!this.idNFeSaida;
  }
  public desagrupando: boolean = false;

  // Agrupamento
  public modoAgrupamento: boolean = false;
  public idsParcelasOrigem: number[] = [];

  public camposObrigatorios: CampoObrigatorioStruct[] = [];
  public lookups: GetLookupsContasReceberResponse | null = null;
  public hasParcelasEmAberto: boolean = false;
  private _temPixAtivo: boolean = false;
  private _anexosPendentes: any[] | null = null;

  get isRateioHabilitado(): boolean {
    return this.form.get('dadosGerais.isRateio')?.value === true;
  }

  get tagsControl(): FormControl {
    return this.form.get('tags') as FormControl;
  }

  /**
   * Tags só são editáveis inline quando o documento tem 1 parcela (à vista ou cada documento
   * recorrente). Na criação sempre mostra. Em parcelado (várias parcelas) a edição é por parcela.
   */
  get mostrarTags(): boolean {
    return !this.isEditMode || this.qtdParcelas === 1;
  }

  get valorTotalDocumento(): number {
    return this.form.get('dadosGerais.valorTotal')?.value || 0;
  }

  get rotaVoltar(): string[] {
    if (this.idContasReceberParcela) {
      return ['/financas/contas-receber/parcela', String(this.idContasReceberParcela)];
    }
    return ['/financas/contas-receber'];
  }

  private _complementaresComponent!: ContasReceberComplementaresComponent;

  @ViewChild(ContasReceberComplementaresComponent)
  set complementaresComponent(comp: ContasReceberComplementaresComponent) {
    this._complementaresComponent = comp;
    if (comp && this._anexosPendentes) {
      comp.anexos = this._anexosPendentes;
      this._anexosPendentes = null;
    }
  }

  get complementaresComponent(): ContasReceberComplementaresComponent {
    return this._complementaresComponent;
  }

  // Scroll Spy Sections
  get sections(): ScrollSpySection[] {
    const base: ScrollSpySection[] = [
      { id: 'dados-gerais', label: 'Dados Gerais' },
      { id: 'rateio', label: 'Centro de Custo' },
      { id: 'parcelamento', label: 'Parcelamento / Repetição' },
      { id: 'complementos', label: 'Arquivos e Complementos' },
    ];
    if (this.isEditMode && this.qtdParcelas === 1) {
      base.splice(3, 0, { id: 'previsao', label: 'Previsão de Recebimento' });
    }
    return base;
  }

  public isRecorrente: boolean = false;

  public currencyMask: any;
  public percentageMask: any;
  public integerMask: any;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private contasReceberService: ContasReceberService,
    private configuracaoCampoObrigatorioService: ConfiguracaoCampoObrigatorioService,
    private pessoaService: PessoaService,
    private municipioService: MunicipioService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private maskService: MaskService
  ) {
    this.currencyMask = { ...this.maskService.getCurrencyMask(), allowNegative: false };
    this.percentageMask = { ...this.maskService.getDecimalMask2(), allowNegative: false };
    this.integerMask = this.maskService.getIntegerMask();
  }

  ngOnInit(): void {
    this.initForm();
    this.carregarLookups();
    this.carregarCamposObrigatorios();
    this.checkRouteParams();

    this.form.get('parcelamento.idFormaRecebimento')?.valueChanges.subscribe(() => {
      this.aplicarValidadorNsuTid();
    });
  }

  private carregarCamposObrigatorios(): void {
    this.configuracaoCampoObrigatorioService.getByTela(TelaFinancasEnum.ContasReceber).subscribe({
      next: res => {
        if (!res.isError && res.listaTelaComCampos?.length) {
          this.camposObrigatorios = res.listaTelaComCampos[0].listaCampos;
          this.aplicarValidadoresDinamicos();
        }
      },
      error: () => {
        /* silent — não bloqueia o formulário */
      },
    });
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  private aplicarValidadoresDinamicos(): void {
    // Centro de custo delegado ao filho via @Input isCentroCustoObrigatorio
    this.aplicarValidator(this.form.get('dadosGerais.idVendedor'), 'id_vendedor');
    this.aplicarValidator(this.form.get('parcelamento.idFormaRecebimento'), 'id_forma_recebimento');
    this.aplicarValidator(this.form.get('parcelamento.idContaFinanceira'), 'id_conta_financeira');
    this.aplicarValidator(this.form.get('complementos.observacao'), 'observacoes_html');
    this.aplicarValidadorNsuTid();
  }

  private aplicarValidator(control: AbstractControl | null, nomeTecnico: string): void {
    if (!control) return;
    if (this.isObrigatorio(nomeTecnico)) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  // NSU/TID só é exigível quando a forma de recebimento selecionada é compatível (Cartão de Crédito/Débito)
  // — mesma obrigatoriedade condicional já usada em Contas a Pagar para o Código de Barras. Reavaliado tanto
  // no carregamento (config da tela) quanto a cada troca de Forma de Recebimento, para não deixar o campo
  // obrigatório "presa" quando o usuário troca pra Dinheiro depois de escolher Cartão.
  private aplicarValidadorNsuTid(): void {
    const control = this.form.get('complementos.nsuTid');
    if (!control) return;
    if (this.isObrigatorio('nsu_tid') && this.formaRecebimentoCompativelComNsuTid()) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private formaRecebimentoCompativelComNsuTid(): boolean {
    const idForma = this.form.get('parcelamento.idFormaRecebimento')?.value;
    if (!idForma) return false;
    const forma = (this.lookups?.formasRecebimento ?? []).find(
      (f: any) => f.id === idForma || f.idFormaRecebimento === idForma
    );
    if (!forma) return false;
    const codigo = String(forma.codigoPagamentoSefaz);
    return codigo === CodigoSefazEnum.CartaoCredito || codigo === CodigoSefazEnum.CartaoDebito;
  }

  private carregarLookups(): void {
    this.isLoadingLookups = true;
    this.contasReceberService.getLookups().subscribe({
      next: res => {
        this.isLoadingLookups = false;
        if (!res.isError) {
          this.lookups = res;
          this.preSelecionarFormaRecebimentoPadrao();
        }
      },
      error: () => {
        this.isLoadingLookups = false;
        this.alertService.show('Erro', 'Não foi possível carregar as listas de seleção.', AlertType.error);
      },
    });
  }

  // Pré-seleciona a forma de recebimento marcada como favorita (isFavorito) só na criação de um lançamento
  // novo — na edição, mantém a forma de recebimento já salva.
  private preSelecionarFormaRecebimentoPadrao(): void {
    if (this.isEditMode) return;

    const idFormaRecebimentoControl = this.form.get('parcelamento.idFormaRecebimento');
    if (!idFormaRecebimentoControl || idFormaRecebimentoControl.value) return;

    const favorita = this.lookups?.formasRecebimento?.find((f: any) => f.isFavorito);
    if (favorita) {
      idFormaRecebimentoControl.setValue(favorita.idFormaRecebimento ?? favorita.id);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Estrutura raiz mantida no Pai, os filhos se conectam aqui.
      // 1. Dados Gerais
      dadosGerais: this.fb.group({
        idCliente: [null, Validators.required],
        dataEmissao: ['', Validators.required],
        dataCompetencia: ['', Validators.required],
        valorTotal: [null, [Validators.required, Validators.min(0.01)]],
        descricao: ['', Validators.required],
        idCategoriaFinanceira: [null, Validators.required],
        idCentroCusto: [null],
        idVendedor: [null],
        isRateio: [false],
        numeroDocumento: [''],
      }),

      // 2. Rateio
      rateio: this.fb.group({
        itens: this.fb.array([]),
      }),

      // 3. Parcelamento/Recorrência
      parcelamento: this.fb.group({
        isRecorrente: [false],

        // Dados de parcelamento
        idTipoParcelamento: [null], // ex: À vista, 2x, etc.
        dataVencimento: ['', Validators.required],
        intervaloDiasParcelas: [null],
        idFormaRecebimento: [null],
        idContaFinanceira: [null],
        isRecebido: [false],
        parcelas: this.fb.array([]), // Tabelinha preview

        // Dados de recorrencia
        recorrenciaIntervalo: [null],
        idFrequenciaRecorrencia: [null], // 1=Dia,2=Semana,3=Mes,4=Ano
        recorrenciaDiaVencimento: [null],
        recorrenciaQuantidade: [null],
      }),

      // 4. Previsão de Recebimento
      previsao: this.fb.group({
        datetimePrevisao: [''],
        valorJuros: [0],
        valorMulta: [0],
        valorDesconto: [0],
        valorAcrescimo: [0],
      }),

      // 5. Complementos
      complementos: this.fb.group({
        observacao: [''],
        nsuTid: [''],
      }),

      // 6. Tags (aplicadas a todas as parcelas na criação; por parcela na edição)
      tags: [[]],
    });
  }

  private checkRouteParams(): void {
    // Parâmetros matriz: /cadastrar;idContasReceber=123
    const paramIdConta = this.route.snapshot.paramMap.get('idContasReceber');
    // Pode vir com a parcela para edição específica
    const paramIdParcela = this.route.snapshot.paramMap.get('idContasReceberParcela');

    if (paramIdConta) {
      this.isEditMode = true;
      this.idContasReceber = Number(paramIdConta);

      if (paramIdParcela) {
        this.idContasReceberParcela = Number(paramIdParcela);
      }

      this.loadData();
    }

    // Agrupamento via queryParams
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('modoAgrupamento') === 'true') {
      this.modoAgrupamento = true;
      const idCliente = Number(qp.get('idCliente') || 0);
      const valorTotal = Number(qp.get('valorTotal') || 0);
      const idsStr = qp.get('idsParcelasOrigem');
      this.idsParcelasOrigem = idsStr ? JSON.parse(idsStr) : [];

      // Pré-preencher dados gerais
      this.form.get('dadosGerais.idCliente')?.setValue(idCliente);
      this.form.get('dadosGerais.valorTotal')?.setValue(valorTotal);
      this.form.get('dadosGerais.descricao')?.setValue('Documento Consolidado (Agrupamento)');
      this.form.get('dadosGerais.dataEmissao')?.setValue(new Date());
      this.form.get('dadosGerais.dataCompetencia')?.setValue(new Date());

      // Agrupamento não pode ser recorrente
      this.form.get('parcelamento.isRecorrente')?.setValue(false);
      this.form.get('parcelamento.isRecorrente')?.disable();
    }
  }

  private loadData(): void {
    if (!this.idContasReceber) return;

    this.isLoading = true;
    this.contasReceberService.getById(this.idContasReceber).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar os dados.',
            AlertType.error
          );
          this.router.navigate(['/financas/contas-receber']);
          return;
        }

        if (res) {
          const dados = res; // A Resposta é o proprio objeto ContasReceberGetByIdResponse

          this.qtdParcelas = dados.parcelas?.length ?? 0;
          this.isAgrupado = dados.isAgrupado ?? false;
          this.idVenda = dados.idVenda ?? null;
          this.idNFeSaida = dados.idNFeSaida ?? null;
          this.numeroDocumentoVenda = dados.numeroDocumento ?? null;

          // Verificar se há parcelas em aberto para habilitar botão de renegociação
          if (dados.parcelas && dados.parcelas.length > 0) {
            this.hasParcelasEmAberto = dados.parcelas.some((p: any) => p.idStatusContasReceber === 1);
            this._temPixAtivo = dados.temPixAtivo ?? false;
          }

          // Popula FormArray de parcelas quando há múltiplas (parcelado)
          if (dados.parcelas && dados.parcelas.length > 1) {
            const parcelasFormArray = this.form.get(
              'parcelamento.parcelas'
            ) as import('@angular/forms').FormArray;
            parcelasFormArray.clear();
            dados.parcelas.forEach((p: any) => {
              parcelasFormArray.push(
                this.fb.group({
                  idContasReceberParcela: [p.idContasReceberParcela],
                  nroParcela: [p.nroParcela],
                  idStatusContasReceber: [p.idStatusContasReceber],
                  dataVencimento: [p.datetimeVencimento ? new Date(p.datetimeVencimento) : null],
                  valor: [p.valor],
                  descricao: [p.descricao],
                })
              );
            });
          }

          // 1. Dados Gerais
          this.form.get('dadosGerais')?.patchValue({
            idCliente: dados.idPessoaCliente,
            dataEmissao: dados.dataEmissao ? new Date(dados.dataEmissao) : '',
            dataCompetencia: dados.dataCompetencia ? new Date(dados.dataCompetencia) : '',
            valorTotal: dados.valorTotal,
            descricao: dados.descricaoOriginal,
            idCategoriaFinanceira: dados.idCategoriaFinanceira,
            idVendedor: dados.idVendedor,
            isRateio: dados.hasRateio,
            numeroDocumento: dados.numeroDocumento,
          });

          // 2. Rateio / Centro de Custo
          if (dados.hasRateio) {
            const itensArray = this.form.get('rateio.itens') as import('@angular/forms').FormArray;
            itensArray.clear();

            if (dados.rateios && dados.rateios.length > 0) {
              dados.rateios.forEach((r: any) => {
                itensArray.push(
                  this.fb.group({
                    idCentroCusto: [r.idCentroCusto, Validators.required],
                    valor: [r.valor],
                    percentual: [r.percentual],
                  })
                );
              });
            }
          } else if (dados.rateios && dados.rateios.length === 1) {
            // Centro de custo único (sem rateio): extrair do array de rateios
            this.form.get('dadosGerais.idCentroCusto')?.setValue(dados.rateios[0].idCentroCusto);
          }

          // 3. Parcelamento / Recorrencia / Complementos Baseado na 1a parcela se houver
          if (dados.parcelas && dados.parcelas.length > 0) {
            // Se houver uma parcela especifica passada na rota (edição pontual), usar ela. Senão considerar a primeira.
            const pAlvo = this.idContasReceberParcela
              ? dados.parcelas.find((p: any) => p.idContasReceberParcela === this.idContasReceberParcela)
              : dados.parcelas[0];

            if (pAlvo) {
              // Preencher Previsao
              this.form.get('previsao')?.patchValue({
                datetimePrevisao: pAlvo.datetimePrevisao ? new Date(pAlvo.datetimePrevisao) : '',
                valorJuros: pAlvo.valorJurosPrevisto || 0,
                valorMulta: pAlvo.valorMultaPrevisto || 0,
                valorDesconto: pAlvo.valorDescontoPrevisto || 0,
                valorAcrescimo: pAlvo.valorAcrescimoPrevisto || 0,
              });

              // Preencher Parcelamento (parcela usa datetimeVencimento)
              this.form.get('parcelamento')?.patchValue({
                idTipoParcelamento: dados.idTipoParcelamento,
                dataVencimento: pAlvo.datetimeVencimento ? new Date(pAlvo.datetimeVencimento) : '',
                intervaloDiasParcelas: dados.intervaloDiasParcelas,
                idFormaRecebimento: pAlvo.idFormaRecebimento,
                idContaFinanceira: pAlvo.idContaFinanceira,
                isRecebido: pAlvo.isRecebido,
              });

              // Preencher Complementos
              this.form.get('complementos')?.patchValue({
                observacao: pAlvo.observacoesHtml || '',
                nsuTid: pAlvo.nsuTid || '',
              });

              // Tags da parcela alvo (relevante quando há 1 parcela — edição inline)
              this.form.get('tags')?.setValue(pAlvo.tags ?? []);
            }
          }

          // Anexos do documento — guarda em _anexosPendentes; o setter do ViewChild aplica quando o componente renderizar
          if (dados.anexosDocumento && dados.anexosDocumento.length > 0) {
            this._anexosPendentes = dados.anexosDocumento.map((a: any) => ({
              idContasReceberAnexo: a.idContasReceberAnexo,
              nomeArquivo: a.nomeArquivo || 'arquivo',
              tamanhoBytes: a.tamanhoBytes || 0,
              urlArquivo: a.urlArquivo,
            }));
          }

          // Se for recorrente, desabilitamos a edição de recorrencia na UI (Escopo inicial de apenas visibilidade)
          if (dados.idConfiguracaoRecorrenciaReceber > 0) {
            this.isRecorrente = true;
            this.form.get('parcelamento.isRecorrente')?.setValue(true);
            if (dados.recorrenciaInfo) {
              this.form.get('parcelamento.recorrenciaIntervalo')?.setValue(dados.recorrenciaInfo.intervalo);
              this.form
                .get('parcelamento.idFrequenciaRecorrencia')
                ?.setValue(dados.recorrenciaInfo.idFrequenciaRecorrencia);
              this.form
                .get('parcelamento.recorrenciaQuantidade')
                ?.setValue(dados.recorrenciaInfo.quantidadeVezes);
              this.form
                .get('parcelamento.recorrenciaDiaVencimento')
                ?.setValue(dados.recorrenciaInfo.diaVencimentoFixo);
            }
          }

          // Gerado pelo pedido de venda: o documento só muda pelo pedido (o Salvar já fica desabilitado), então a
          // tela abre inteira em leitura, como o cadastro do lançamento da movimentação gerada pela venda.
          if (this.isOrigemDocumento) this.form.disable({ emitEvent: false });
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a Conta a Receber.', AlertType.error);
        this.router.navigate(['/financas/contas-receber']);
      },
    });
  }

  public salvar(): void {
    if (this.desagrupando) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Verifique os campos em vermelho.', AlertType.warning);
      return;
    }

    // Helper functions
    const dGerais = this.form.get('dadosGerais')?.value;
    const rateio = this.form.get('rateio')?.value;
    const parc = this.form.get('parcelamento')?.value;

    if (dGerais.dataEmissao && parc.dataVencimento && new Date(parc.dataVencimento) < new Date(dGerais.dataEmissao)) {
      this.alertService.show('Atenção', 'A data de vencimento não pode ser anterior à data de emissão.', AlertType.warning);
      return;
    }

    if (!parc.isRecorrente && parc.parcelas && parc.parcelas.length > 0) {
      const somaParcelas = parc.parcelas.reduce((soma: number, p: any) => soma + (p.valor || 0), 0);
      if (UtilService.arredondarAbnt(somaParcelas, 2) !== UtilService.arredondarAbnt(dGerais.valorTotal || 0, 2)) {
        this.alertService.show('Atenção', 'A soma das parcelas precisa ser igual ao Valor Total do documento.', AlertType.warning);
        return;
      }
    }

    if (dGerais.isRateio) {
      const itensRateio: any[] = rateio.itens || [];

      if (!itensRateio.length) {
        this.alertService.show('Atenção', 'Adicione ao menos uma linha de rateio por centro de custo.', AlertType.warning);
        return;
      }

      const possuiLinhaInvalida = itensRateio.some(
        item => !item.idCentroCusto || (!item.valor && !item.percentual)
      );
      if (possuiLinhaInvalida) {
        this.alertService.show('Atenção', 'Preencha centro de custo e valor ou percentual em todas as linhas do rateio.', AlertType.warning);
        return;
      }

      const somaPercentual = itensRateio.reduce((soma: number, item: any) => soma + (Number(item.percentual) || 0), 0);
      if (Math.abs(somaPercentual - 100) > 0.01) {
        this.alertService.show('Atenção', 'O rateio por centro de custo precisa totalizar 100%.', AlertType.warning);
        return;
      }
    }

    this.isSaving = true;
    const prev = this.form.get('previsao')?.value;
    const comp = this.form.get('complementos')?.value;

    const dtEmissao = dGerais.dataEmissao ? new Date(dGerais.dataEmissao).toISOString() : '';
    const dtComp = dGerais.dataCompetencia ? new Date(dGerais.dataCompetencia).toISOString() : '';
    const dtVenc = parc.dataVencimento ? new Date(parc.dataVencimento).toISOString() : '';
    const dtPrev = prev.datetimePrevisao ? new Date(prev.datetimePrevisao).toISOString() : null;

    if (this.isEditMode) {
      this.isSaving = false;
      if (this._temPixAtivo) {
        this.dialog.open(ConfirmModalComponent, {
          width: '440px',
          data: {
            title: 'Atenção — Pix Ativo',
            innerHtml: 'Esta parcela possui um Pix ativo. Ao prosseguir com a edição, a cobrança Pix será cancelada automaticamente.',
            buttonConfirm: 'Prosseguir',
            buttonClose: 'Voltar',
          },
        }).afterClosed().subscribe((res: any) => {
          if (res?.confirm) this.prosseguirEdicao(dGerais, rateio, parc, prev, comp, dtEmissao, dtComp, dtVenc, dtPrev);
        });
      } else {
        this.prosseguirEdicao(dGerais, rateio, parc, prev, comp, dtEmissao, dtComp, dtVenc, dtPrev);
      }
    } else {
      // Cadastrar Novo
      const requestCreate: any = {
        idPessoaCliente: dGerais.idCliente,
        dataEmissao: dtEmissao,
        dataCompetencia: dtComp,
        valorTotal: dGerais.valorTotal,
        descricaoOriginal: dGerais.descricao,
        numeroDocumento: dGerais.numeroDocumento,
        idCategoriaFinanceira: dGerais.idCategoriaFinanceira,
        idVendedor: dGerais.idVendedor,
        hasRateio: dGerais.isRateio,
        idCentroCusto: !dGerais.isRateio ? dGerais.idCentroCusto : null,
        rateios: dGerais.isRateio && rateio.itens.length > 0 ? rateio.itens : null,

        isRecorrente: parc.isRecorrente,
        observacoesHtml: comp.observacao,
        nsuTid: comp.nsuTid || null,
        tags: this.form.get('tags')?.value ?? [],
        anexos:
          this.complementaresComponent?.anexos?.map(a => ({
            idContasReceberAnexo: a.idContasReceberAnexo || null,
            nomeArquivo: a.nomeArquivo,
            tamanhoBytes: a.tamanhoBytes,
            base64: a.base64 || null,
          })) || [],
      };

      // Se for recorrente, anexa objeto RecorrenciaRequest
      if (parc.isRecorrente) {
        requestCreate.recorrencia = {
          intervalo: parc.recorrenciaIntervalo || 1,
          idFrequenciaRecorrencia: parc.idFrequenciaRecorrencia || 3, // default mes
          diaVencimentoFixo: parc.recorrenciaDiaVencimento || 0,
          quantidadeVezes: parc.recorrenciaQuantidade || 1,
          idFormaRecebimento: parc.idFormaRecebimento,
          idContaFinanceira: parc.idContaFinanceira,
          dataInicio: dtVenc,
        };
      } else {
        // Se nao for recorrente, anexa objeto ParcelamentoRequest
        const parcelamentoObj: any = {
          idTipoParcelamento: parc.idTipoParcelamento || 1,
          dataVencimento: dtVenc,
          intervaloDiasParcelas: parc.intervaloDiasParcelas,
          idFormaRecebimento: parc.idFormaRecebimento,
          idContaFinanceira: parc.idContaFinanceira,
          isRecebido: parc.isRecebido ? true : false,
        };

        // Se o grid de simulação foi preenchido, enviar parcelas personalizadas
        const parcelasArray = this.form.get('parcelamento.parcelas') as import('@angular/forms').FormArray;
        if (parcelasArray && parcelasArray.length > 0) {
          parcelamentoObj.parcelas = parcelasArray.controls.map((ctrl: any) => ({
            nroParcela: ctrl.get('nroParcela')?.value,
            dataVencimento: ctrl.get('dataVencimento')?.value
              ? new Date(ctrl.get('dataVencimento').value).toISOString()
              : null,
            valor: ctrl.get('valor')?.value || 0,
            descricao: ctrl.get('descricao')?.value,
          }));
        }

        requestCreate.parcelamento = parcelamentoObj;
      }

      // Determinar se é agrupamento ou criação normal
      if (this.modoAgrupamento) {
        const agrupamentoRequest = {
          documento: requestCreate,
          idsParcelasOrigem: this.idsParcelasOrigem,
          observacaoAgrupamento: comp.observacao || null,
        };

        this.contasReceberService.agrupar(agrupamentoRequest).subscribe({
          next: res => {
            this.isSaving = false;
            if (res.isError) {
              this.alertService.show(
                'Erro',
                res.errorDescription || 'Erro ao agrupar documentos',
                AlertType.error
              );
              return;
            }
            // Avisos do cancelamento das cobranças (PIX/boleto) das parcelas originais
            if (res.avisoDescricao) {
              const avisoHtml = res.avisoDescricao.split(' | ').join('<br><br>');
              this.dialog.open(ConfirmModalComponent, {
                data: { title: 'Atenção — Cobrança', innerHtml: avisoHtml, buttonClose: 'Entendi' }
              });
            }
            this.alertService.show(
              'Sucesso',
              'Parcelas agrupadas com sucesso! As parcelas originais foram canceladas.',
              AlertType.success
            );
            this.router.navigate(['/financas/contas-receber']);
          },
          error: () => {
            this.isSaving = false;
            this.alertService.show(
              'Erro',
              'Ocorreu um erro ao comunicar-se com o servidor.',
              AlertType.error
            );
          },
        });
      } else {
        this.decidirGerarBoletosECriar(requestCreate, parc, dGerais.idCliente);
      }
    }
  }

  /**
   * Se a forma de recebimento for boleto e a conta financeira estiver conectada, pergunta ao usuário
   * se deseja gerar os boletos das parcelas. Conforme a resposta, anexa gerarBoletos + sacado e cria.
   */
  private decidirGerarBoletosECriar(requestCreate: any, parc: any, idCliente: number): void {
    const idForma = parc?.idFormaRecebimento;
    const idConta = parc?.idContaFinanceira;
    const forma = (this.lookups?.formasRecebimento ?? []).find(
      (f: any) => f.id === idForma || f.idFormaRecebimento === idForma
    );
    const isBoleto = !!forma && String(forma.codigoPagamentoSefaz) === CodigoSefazEnum.Boleto;

    // Decide pela forma de recebimento (=boleto) + conta selecionada. Com forma boleto, a conta já vem
    // filtrada nos lookups como vinculada ao boleto ("tem config"). O gate real de "Conectado" é do
    // backend na hora de gerar — se não estiver conectada, volta como "sem integração" no resumo.
    if (!isBoleto || !idConta) {
      this.executarCreate(requestCreate);
      return;
    }

    const qtd = this.calcularQtdParcelas(parc);
    const custoUnit = UtilService.arredondarAbnt(this.lookups?.valorBilhetagemBoleto ?? 0, 2);
    const custoTotal = UtilService.arredondarAbnt(qtd * custoUnit, 2);
    const fmt = (v: number) => 'R$ ' + (v ?? 0).toFixed(2).replace('.', ',');

    // Aguardando decisão do usuário — libera o spinner durante o diálogo
    this.isSaving = false;
    this.dialog.open(ConfirmModalComponent, {
      width: '460px',
      data: {
        title: 'Gerar boletos?',
        innerHtml: `A forma de recebimento é <strong>boleto</strong>. Deseja gerar os boletos de <strong>${qtd} parcela(s)</strong> agora?`
          + `<br><br>Custo de bilhetagem: <strong>${fmt(custoUnit)}</strong> por boleto — total <strong>${fmt(custoTotal)}</strong>.`
          + `<br><br><small>Em recorrência, será gerado um boleto por ocorrência.</small>`,
        buttonConfirm: 'Sim, gerar boletos',
        buttonClose: 'Não, gerar depois',
      },
    }).afterClosed().subscribe((res: any) => {
      if (res?.confirm) {
        this.pessoaService.getById(idCliente).subscribe({
          next: pessoaResp => {
            if (pessoaResp.isError || !pessoaResp.pessoa) {
              this.isSaving = false;
              this.alertService.show('Erro', pessoaResp.errorDescription || 'Não foi possível obter os dados do cliente.', AlertType.error);
              return;
            }

            const enderecos = pessoaResp.enderecos ?? [];
            const endereco = enderecos.find(item => item.isCobranca)
              ?? enderecos.find(item => item.isPrincipal)
              ?? enderecos[0];
            const idMunicipio = endereco?.idMunicipio;
            if (!idMunicipio) {
              this.isSaving = false;
              this.alertService.show('Erro', 'Cliente sem município no endereço de cobrança ou principal.', AlertType.error);
              return;
            }

            this.municipioService.getByIds([idMunicipio]).subscribe({
              next: municipioResponse => {
                const municipio = municipioResponse.list?.find(item => item.idMunicipio === idMunicipio);
                if (municipioResponse.isError || !municipio) {
                  this.isSaving = false;
                  this.alertService.show('Erro', municipioResponse.errorDescription || 'Não foi possível resolver o município do cliente.', AlertType.error);
                  return;
                }

                requestCreate.gerarBoletos = true;
                requestCreate.sacado = SacadoBoletoStruct.fromPessoaResponse(pessoaResp, municipio);
                this.isSaving = true;
                this.executarCreate(requestCreate);
              },
              error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Não foi possível consultar o município do cliente.', AlertType.error);
              }
            });
          },
          error: () => {
            this.isSaving = false;
            this.alertService.show('Erro', 'Não foi possível obter os dados do cliente.', AlertType.error);
          }
        });
      } else {
        this.isSaving = true;
        this.executarCreate(requestCreate);
      }
    });
  }

  /** Quantidade de parcelas que serão geradas (recorrência, grid de simulação ou tipo de parcelamento). */
  private calcularQtdParcelas(parc: any): number {
    if (parc?.isRecorrente) {
      return Number(parc.recorrenciaQuantidade) || 1;
    }
    const parcelasArray = this.form.get('parcelamento.parcelas') as import('@angular/forms').FormArray;
    if (parcelasArray && parcelasArray.length > 0) return parcelasArray.length;
    const tipo = (this.lookups?.tiposParcelamento ?? []).find(
      (t: any) => t.idTipoParcelamento === parc?.idTipoParcelamento || t.id === parc?.idTipoParcelamento
    );
    return Number(tipo?.quantidadeParcelas) || 1;
  }

  private executarCreate(requestCreate: any): void {
    this.contasReceberService.create(requestCreate).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao inserir o documento', AlertType.error);
          return;
        }
        const bg = res.boletosGerados;
        if (bg && (bg.sucesso || bg.falha || bg.semIntegracao)) {
          let msg = `Documento inserido. Boletos: ${bg.sucesso} gerado(s)`;
          if (bg.falha) msg += `, ${bg.falha} com falha`;
          if (bg.semIntegracao) msg += `, ${bg.semIntegracao} sem integração ativa`;
          msg += '.';
          this.alertService.show('Sucesso', msg, bg.falha ? AlertType.warning : AlertType.success);
        } else {
          this.alertService.show('Sucesso', 'Documento inserido com sucesso!', AlertType.success);
        }
        this.router.navigate(['/financas/contas-receber']);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao comunicar-se com o servidor.', AlertType.error);
      },
    });
  }

  private prosseguirEdicao(dGerais: any, rateio: any, parc: any, prev: any, comp: any, dtEmissao: string, dtComp: string, dtVenc: string, dtPrev: string | null): void {
    if (this.isRecorrente) {
      const dialogRef = this.dialog.open(ConfirmAcaoRecorrenteModalComponent, {
        width: '500px',
        data: {
          title: 'Atenção, Conta Recorrente!',
          description: 'Você está editando uma Conta a Receber que possui recorrência. O que deseja fazer com as contas em aberto vinculadas a essa conta?',
          buttonConfirm: 'Salvar',
          buttonClose: 'Cancelar',
        },
      });
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result?.confirm) {
          this.avisarCobrancaEntaoEditar(result.tipoProcessamento, true, () =>
            this.executarUpdate(result.tipoProcessamento, dGerais, rateio, parc, prev, comp, dtEmissao, dtComp, dtVenc, dtPrev));
        }
      });
    } else {
      this.avisarCobrancaEntaoEditar(TipoProcessamentoRecorrenciaEnum.ApenasEsta, false, () =>
        this.executarUpdate(TipoProcessamentoRecorrenciaEnum.ApenasEsta, dGerais, rateio, parc, prev, comp, dtEmissao, dtComp, dtVenc, dtPrev));
    }
  }

  /**
   * Avisa ANTES de salvar quando a edição vai mexer numa parcela com cobrança ativa (PIX/boleto) — a alteração
   * cancela/baixa a cobrança. Discrimina baixa automática × manual. Em recorrência de escopo múltiplo, alerta que
   * o mesmo vale para as demais parcelas afetadas. Se não houver nada a avisar, prossegue direto.
   */
  private avisarCobrancaEntaoEditar(tipoProcessamento: number, isRecorrente: boolean, onProsseguir: () => void): void {
    if (!this.idContasReceberParcela) { onProsseguir(); return; }
    const recorrenteMultiplo = isRecorrente && tipoProcessamento !== TipoProcessamentoRecorrenciaEnum.ApenasEsta;

    this.contasReceberService.getCobrancaAtivoInfo([this.idContasReceberParcela])
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const info = res?.itens?.[0];
        let aviso = '';
        if (info?.temPixAtivo)
          aviso += 'Esta parcela possui um Pix ativo que será cancelado.';
        if (info?.boletoJaPago)
          aviso += (aviso ? '<br><br>' : '') + 'Esta parcela possui um boleto já pago — ele não será cancelado; eventual devolução é manual.';
        else if (info?.temBoletoAtivo)
          aviso += (aviso ? '<br><br>' : '') + (info.boletoSuportaBaixaAutomatica
            ? 'Esta parcela possui um boleto ativo — a baixa será solicitada ao banco automaticamente.'
            : 'Esta parcela possui um boleto ativo e o banco <strong>não permite baixa automática</strong> — você precisará baixá-lo manualmente no portal do banco.');
        if (recorrenteMultiplo)
          aviso += (aviso ? '<br><br>' : '') + 'O mesmo se aplica às demais parcelas afetadas da recorrência (boletos de bancos sem baixa automática precisarão ser baixados manualmente).';

        if (!aviso) { onProsseguir(); return; }

        this.dialog.open(ConfirmModalComponent, {
          width: '460px',
          data: {
            title: 'Atenção — Cobrança',
            innerHtml: aviso + '<br><br>Deseja continuar?',
            buttonConfirm: 'Continuar',
            buttonClose: 'Voltar',
          }
        }).afterClosed().subscribe((r: any) => { if (r?.confirm) onProsseguir(); });
      });
  }

  private executarUpdate(
    tipoEdicaoRecorrencia: number,
    dGerais: any,
    rateio: any,
    parc: any,
    prev: any,
    comp: any,
    dtEmissao: string,
    dtComp: string,
    dtVenc: string,
    dtPrev: string | null
  ): void {
    this.isSaving = true;

    const requestUpdate: any = {
      idContasReceber: this.idContasReceber!,
      idContasReceberParcela: this.idContasReceberParcela,
      tipoEdicaoRecorrencia: tipoEdicaoRecorrencia,
      idPessoaCliente: dGerais.idCliente,
      dataEmissao: dtEmissao,
      dataCompetencia: dtComp,
      valorTotal: dGerais.valorTotal,
      descricaoOriginal: dGerais.descricao,
      numeroDocumento: dGerais.numeroDocumento,
      idCategoriaFinanceira: dGerais.idCategoriaFinanceira,
      idVendedor: dGerais.idVendedor,
      hasRateio: dGerais.isRateio,
      idCentroCusto: !dGerais.isRateio ? dGerais.idCentroCusto : null,
      rateios: dGerais.isRateio && rateio.itens.length > 0 ? rateio.itens : null,
      dataVencimento: dtVenc,
      datetimePrevisao: dtPrev,
      idFormaRecebimento: parc.idFormaRecebimento,
      idContaFinanceira: parc.idContaFinanceira,
      valorJurosPrevisto: prev.valorJuros,
      valorMultaPrevisto: prev.valorMulta,
      valorDescontoPrevisto: prev.valorDesconto,
      valorAcrescimoPrevisto: prev.valorAcrescimo,
      isRecebido: parc.isRecebido ? true : false,
      observacoesHtml: comp.observacao,
      nsuTid: comp.nsuTid || null,
      // Só envia tags quando editáveis inline (1 parcela). Em parcelado, null = backend não mexe nas tags.
      tags: this.mostrarTags ? (this.form.get('tags')?.value ?? []) : null,
      anexos:
        this.complementaresComponent?.anexos?.map(a => ({
          idContasReceberAnexo: a.idContasReceberAnexo || null,
          nomeArquivo: a.nomeArquivo,
          tamanhoBytes: a.tamanhoBytes,
          base64: a.base64 || null,
        })) || [],
    };

    this.contasReceberService.update(requestUpdate).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao atualizar o documento',
            AlertType.error
          );
          return;
        }
        if (res.avisoDescricao) {
          const pixHtml = res.avisoDescricao.split(' | ').join('<br><br>');
          this.dialog.open(ConfirmModalComponent, {
            data: { title: 'Atenção — Cobrança', innerHtml: pixHtml, buttonClose: 'Entendi' }
          });
        }
        this.alertService.show('Sucesso', 'Documento atualizado com sucesso!', AlertType.success);
        this.router.navigate(this.rotaVoltar);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao comunicar-se com o servidor.', AlertType.error);
      },
    });
  }

  public abrirRenegociacao(): void {
    if (!this.idContasReceber) return;

    // Buscar valor em aberto do backend
    this.contasReceberService.getValorRenegociacao(this.idContasReceber).subscribe({
      next: (res: any) => {
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao buscar valor para renegociação.',
            AlertType.error
          );
          return;
        }

        const dialogRef = this.dialog.open(ModalRenegociacaoParcelasComponent, {
          maxWidth: '95vw',
          maxHeight: '95vh',
          disableClose: true,
          data: {
            idContasReceber: this.idContasReceber,
            idCliente: this.form.get('dadosGerais.idCliente')?.value,
            valorEmAberto: res.valorEmAberto,
            valorTotal: res.valorTotal,
            descricaoDocumento: this.form.get('dadosGerais.descricao')?.value || '',
            listaTiposParcelamento: this.lookups?.tiposParcelamento ?? [],
            listaContaFinanceira: this.lookups?.contasFinanceiras ?? [],
            listaFormaRecebimento: this.lookups?.formasRecebimento ?? [],
            valorBilhetagemBoleto: this.lookups?.valorBilhetagemBoleto ?? 0,
            currencyMask: this.currencyMask,
          },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
          if (result?.success) {
            // Avisos do cancelamento das cobranças (PIX/boleto) das parcelas canceladas na renegociação
            if (result.avisoDescricao) {
              const avisoHtml = result.avisoDescricao.split(' | ').join('<br><br>');
              this.dialog.open(ConfirmModalComponent, {
                data: { title: 'Atenção — Cobrança', innerHtml: avisoHtml, buttonClose: 'Entendi' }
              });
            }
            // Recarregar os dados do documento após renegociação
            this.loadData();
          }
        });
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      },
    });
  }

  public desagrupar(): void {
    if (!this.idContasReceber) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Desagrupar este lançamento?',
        innerHtml:
          'Ao continuar, as parcelas originais serão reabertas e as <b>cobranças ativas deste agrupamento (PIX ou boleto) serão canceladas automaticamente</b>. O histórico das cobranças será preservado para auditoria.',
        buttonConfirm: 'Sim, desagrupar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result?.confirm) return;

      this.desagrupando = true;
      this.contasReceberService.desagrupar(this.idContasReceber!).subscribe({
        next: (res) => {
          this.desagrupando = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Erro ao desagrupar.', AlertType.error);
            return;
          }
          if (res.avisoDescricao) {
            const avisoHtml = res.avisoDescricao.split(' | ').join('<br><br>');
            this.dialog.open(ConfirmModalComponent, {
              data: { title: 'Atenção — Cobrança', innerHtml: avisoHtml, buttonClose: 'Entendi' },
            });
          }
          this.alertService.show('Sucesso', 'Agrupamento desfeito com sucesso.', AlertType.success);
          this.router.navigate(this.rotaVoltar);
        },
        error: () => {
          this.desagrupando = false;
          this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
        },
      });
    });
  }

  public cancelar(): void {
    this.router.navigate(this.rotaVoltar);
  }
}
